import { transformAllContentItems } from "./library";
import {
    TYPE_LINEUP,
    TYPE_CUSTOMHTML,
    TYPE_SPONSORED,
    TYPE_NEWSLETTER,
    TYPE_ADELEMENT,
    TYPE_CLIENTSIDE,
    TYPE_CONTESTPROMO,
    TYPE_CTA,
    TYPE_SCHEDULE,
    TYPE_RADIOBAR,
    TYPE_BROADCAST_SCHEDULE,
    TYPE_CONTENT_QUERY_WIDGET,
    TYPE_CONTENT_PACKAGE,
} from "../../constants/page";
import { formatHeadLinesWithSlug } from "./utils";
import log from "../logger/library";
import { parseContentQueryWidget } from "./ContentQueryWidget/library";
import { parseContentPackage } from "../../components/Card/components/CardContentPackage/utils";

/**
 * The general idea of the parser functions is that they may take a response payload and restructure it,
 * but should not be changing actual data values.  Data changing functionality should be done in data transform function
 * that are invoked by the parsers
 */

export const ParsingFunctions = new Map([
    [TYPE_LINEUP, parseLineups],
    [TYPE_NEWSLETTER, parseGenericWidget],
    [TYPE_CUSTOMHTML, parseGenericWidget],
    [TYPE_SPONSORED, parseSponsoredWidget],
    [TYPE_ADELEMENT, parseGenericWidget],
    [TYPE_CLIENTSIDE, parseClientSideWidget],
    [TYPE_CONTESTPROMO, parseGenericWidget],
    [TYPE_CTA, parseGenericWidget],
    [TYPE_RADIOBAR, parseGenericWidget],
    [TYPE_BROADCAST_SCHEDULE, parseGenericWidget],
    [TYPE_SCHEDULE, parseSchedule],
    [TYPE_CONTENT_QUERY_WIDGET, parseContentQueryWidget],
    [TYPE_CONTENT_PACKAGE, (item) => item],
]);

/**
 * Return a new object with the nested contents of item.widget flattened with type:displayType and sourceId.
 *
 * @param {object} item
 * @param {number} item.sourceId
 * @param {object} item.pageWidget
 */
export function parseClientSideWidget(item) {
    if (!item || !item.type || !item.sourceId) {
        return;
    }

    const pageWidget = item.pageWidget;

    if (!pageWidget || !pageWidget.metadata) {
        return;
    }

    const { sourceId } = item;

    return {
        type: pageWidget.metadata.displayType,
        sourceId,
        ...pageWidget.metadata,
    };
}

/**
 * Returns a new object with custom data structure used in <SponsorInfo />
 * Data structure is mimicking transformSponsorWidget() without the Immutable type
 */
export function parseSponsoredWidget(item) {
    if (!item || !item.type || !item.sourceId) {
        return;
    }

    const pageWidget = item.pageWidget;

    if (!pageWidget || !pageWidget.metadata) {
        return;
    }

    const sponsors = pageWidget.metadata.sponsors || [];
    return {
        type: item.type,
        sourceId: item.sourceId,
        heading: pageWidget.metadata.label || "",
        sponsorList: sponsors.map((sponsor) => {
            return {
                link: sponsor.url || "",
                title: sponsor.name || "",
                imageURL: sponsor?.logo?.aspects?.ratioCustom?.url || "",
            };
        }),
    };
}

/**
 * Return a new object with the nested contents of item.widget flattened with type and sourceId.
 * This can be used for parsing any widget from polopoly that has the 'widget' nested property in it's content.
 *
 * @param {object} item
 * @param {string} item.type
 * @param {number} item.sourceId
 * @param {object} item.pageWidget
 */
export function parseGenericWidget(item) {
    if (!item || !item.type || !item.sourceId) {
        return;
    }

    const pageWidget = item.pageWidget;

    if (!pageWidget || !pageWidget.metadata) {
        return;
    }

    const { type, sourceId } = item;

    return { type, sourceId, ...pageWidget.metadata };
}

/**
 * Return a new object with the nested contents of item.widget flattened with type and sourceId.
 * The video lineup is also transformed and flattened into the new object.
 *
 * @param {object} item
 * @param {string} item.type
 * @param {number} item.sourceId
 * @param {object} item.widget
 * @param {object} item.pageWidget
 */

export function parseLineups(item) {
    if (!item) {
        return;
    }

    let featureInstance = item.wrapper?.attribution?.featureInstance;
    featureInstance = /^[A-Za-z\\-]+$/.test(featureInstance)
        ? featureInstance
        : null; // Default null so react will remove the data attributes

    formatHeadLinesWithSlug(item.lineup?.slug, item.content);
    item.content = transformAllContentItems(item.content);

    return {
        ...item,
        wrapper: {
            ...item.wrapper,
            attribution: {
                ...item.wrapper?.attribution,
                featureInstance: featureInstance,
            },
        },
    };
}

export function parseSchedule(item) {
    return item && item.type === TYPE_SCHEDULE && item;
}

const reducePageSectionContents = (page, layout = null) => {
    return (parsedContents, currentContent) => {
        return reduceContent(parsedContents, currentContent, page, layout);
    };
};

export const reduceContent = (parsedContents, currentContent, page) => {
    if (!currentContent) {
        return parsedContents;
    }
    const type = currentContent.type;
    const parseFn = ParsingFunctions.get(type);

    if (parseFn) {
        const parsedContent = parseFn(currentContent, page) ?? null;
        if (parsedContent !== null) {
            parsedContents.push(parsedContent);
        } else {
            log.error("(reduceContent) Unable to parse item");
        }
    }

    return parsedContents;
};

//passing page now, instead of just sections, because some parsing / transforming might want more page level contextual information
export const parsePageSections = (page) => {
    if (!page.sections) {
        return;
    }

    return page.sections.map(({ main, right }) => {
        main = parseContentPackage(main);
        main.content =
            main.content &&
            main.content.reduce(
                reducePageSectionContents(page, main.layout),
                []
            );
        right.content =
            right.content &&
            right.content.reduce(reducePageSectionContents(page), []);

        return { main, right };
    });
};

export const parseContentListingItems = (list, items) => {
    if (!list || !Array.isArray(list) || list.length < 1) {
        return;
    }

    if (!items || Object.keys(items).length < 1) {
        return;
    }

    return Object.entries(list)
        .map(([_, item]) => {
            let graphData = items?.[item.lineupID]?.nodes;

            if (!graphData) {
                return null;
            }

            formatHeadLinesWithSlug(item.lineupSlug, graphData);
            const transformedList = transformAllContentItems(graphData);
            const transformedItem = { ...item, list: transformedList };
            return transformedItem;
        })
        .filter((item) => item !== null);
};
